import { Spin, Result } from "antd";
import Avatar from "boring-avatars";
import { useEffect, useState } from "react";
import { fetchProfile } from "../../data/firestore/profile";
import { Id, Wishlist, Reserved as ReservedDto } from "../../data/types";
import { generatePath, Link } from "react-router";
import { useLoadMultipleWishlists, useReserved } from "../../data/hooks";
import { RoutePaths } from "../routes";
import Button from "../Components/Button";
import DocumentTitle from "react-document-title";
import MobileHeader from "../Components/MobileHeader";
import WishImage from "./Wishlist/WishImage";
import { uniq } from "lodash";
import {
  analyticsLogEvent,
  analyticsReportError,
} from "../../data/firebase/analytics";

interface ReservedProps {
  wishlists: Wishlist[];
  wishlistId: Id;
  wishId: Id;
  unreserve: (w1: Id, w2: Id) => Promise<ReservedDto[]>;
  names: Record<Id, string>;
}

function ReservedGift({
  wishlists,
  wishlistId,
  wishId,
  unreserve,
  names,
}: ReservedProps) {
  const [isLoading, setIsLoading] = useState(false);
  const wishlistLink = generatePath(RoutePaths.specificWishlist, {
    wishlistId,
  });
  const wishlist = wishlists?.find((w) => w.id === wishlistId);
  const wish = wishlist?.items.find((i) => i.id == wishId);
  const onUnreserve = async () => {
    setIsLoading(true);
    try {
      await unreserve(wishlistId, wishId);
      analyticsLogEvent("wish_unreserved", { wishlistId, wishId });
    } catch (e) {
      analyticsReportError(e as Error);
    }
    setIsLoading(false);
  };

  return (
    <div
      className="wish-border flex flex-col gap-4 rounded-2xl border p-4"
      key={wishId}
    >
      <div className="flex w-full flex-row items-center gap-4">
        <Link to={wishlistLink} viewTransition>
          {wishlist ? (
            <Avatar name={wishlist.userId} size={40} variant={"beam"} />
          ) : (
            <Spin />
          )}
        </Link>
        <Link to={wishlistLink} className="grow" viewTransition>
          <div className="H6 wish-light-primary">
            {(wishlist?.userId ? names[wishlist?.userId] : false) || (
              <i>Anonymous</i>
            )}
          </div>
        </Link>
        <Button loading={isLoading} lighter onClick={onUnreserve}>
          ✨ Unreserve
        </Button>
      </div>
      <div className="flex w-full flex-row items-center gap-4">
        <Link
          to={generatePath(RoutePaths.wishlistItem, { wishlistId, wishId })}
        >
          <WishImage imageLink={wish?.imageLink || ""} small />
        </Link>
      </div>
    </div>
  );
}

export default function Reserved() {
  const { isLoaded, reserved, unreserve } = useReserved();
  const wishlists = useLoadMultipleWishlists(
    uniq(reserved.map((r) => r.wishlistId)),
  );

  const [names, setNames] = useState<Record<Id, string>>({});
  useEffect(() => {
    for (const wishlist of wishlists || []) {
      if (!(wishlist.userId in names)) {
        fetchProfile(wishlist.userId)
          .then((friendProfile) =>
            setNames((curNames) => ({
              ...curNames,
              [wishlist.userId]: friendProfile.displayName,
            })),
          )
          .catch((e) => console.error("Reserved > fetch profiles", e));
      }
    }
  }, [wishlists, names]);

  if (!isLoaded) {
    return <Spin />;
  }

  return (
    <DocumentTitle title="Reserved">
      <>
        <MobileHeader title="Your reserved gifts" />
        <div className="flex flex-col gap-8 p-8">
          <div className="flex flex-wrap justify-center gap-8 p-8">
            {!reserved.length && (
              <Result
                icon={<div className="text-5xl">✨</div>}
                title="No reserved gifts!"
                subTitle="Reserve some gifts on your friends' wishlists!"
              />
            )}
            {reserved.map(({ wishId, wishlistId }) => {
              return (
                <ReservedGift
                  names={names}
                  wishlists={wishlists || []}
                  wishId={wishId}
                  wishlistId={wishlistId}
                  unreserve={unreserve}
                  key={wishId}
                />
              );
            })}
          </div>
        </div>
      </>
    </DocumentTitle>
  );
}
